import { Signal, computed } from '@angular/core';
import { Translation, translateObjectSignal } from '@jsverse/transloco';
import FR from '../../../i18n/anc/universes/fr.json';

/**
 * The four universes of the October 2026 art direction (.Cloud, .Infra, .Web,
 * .Entreprise). Copy comes from direction-artistique/04-offre-et-contenus.md and
 * links point to the existing pages of the site. Structure, links and icons
 * live here; the copy lives in src/i18n/anc/universes/<lang>.json and is read
 * through `injectUniverses()`, French being the fallback.
 */
export type UniverseKey = 'cloud' | 'infra' | 'web' | 'ent';

/**
 * Isometric product illustration, by file name in public/img/mega-menu/ (160×160 WebP).
 * Partner logos (Intel, AMD, Plesk, Microsoft, Google) are drawn into the illustration.
 */
export type UniverseIcon =
  | 'cloud-standard'
  | 'cloud-performance'
  | 'cloud-plesk-dedicated'
  | 'plesk'
  | 'infra-datacenter'
  | 'infra-tunnels'
  | 'infra-ddos'
  | 'web-domains'
  | 'web-ssl'
  | 'ent-microsoft'
  | 'ent-google'
  | 'ent-fibre'
  | 'ent-telephony';

export interface UniverseLink {
  path: string;
  queryParams?: Record<string, string>;
}

export interface UniverseItem {
  /** Translation key of the item, unique within its universe. */
  id: string;
  title: string;
  description: string;
  icon: UniverseIcon;
  link: UniverseLink;
}

export interface Universe {
  key: UniverseKey;
  name: string;
  tagline: string;
  description: string;
  link: UniverseLink;
  items: readonly UniverseItem[];
  highlight: { label: string; metric: string; caption: string; link: UniverseLink };
  note: { text: string; detail: string };
  banner: { title: string; caption: string; tags: readonly string[]; link: UniverseLink };
}

/** Copy of one universe, as stored in the translation files. */
interface UniverseCopy {
  tagline?: string;
  description?: string;
  items?: Record<string, { title?: string; description?: string } | undefined>;
  highlight?: { label?: string; metric?: string; caption?: string };
  note?: { text?: string; detail?: string };
  banner?: { title?: string; caption?: string };
}

type UniversesCopy = Partial<Record<UniverseKey, UniverseCopy>>;

interface UniverseStructure {
  key: UniverseKey;
  name: string;
  link: UniverseLink;
  items: readonly { id: string; icon: UniverseIcon; link: UniverseLink }[];
  highlightLink: UniverseLink;
  bannerTags: readonly string[];
  bannerLink: UniverseLink;
}

const STRUCTURE: readonly UniverseStructure[] = [
  {
    key: 'cloud',
    name: '.Cloud',
    link: { path: '/vps-standard', queryParams: { type: 'lxc' } },
    items: [
      {
        id: 'standard',
        icon: 'cloud-standard',
        link: { path: '/vps-standard', queryParams: { type: 'lxc' } },
      },
      {
        id: 'performance',
        icon: 'cloud-performance',
        link: { path: '/vps-performance', queryParams: { type: 'lxc' } },
      },
      { id: 'pleskDedicated', icon: 'cloud-plesk-dedicated', link: { path: '/plesk-dedicated' } },
    ],
    highlightLink: { path: '/antiddos' },
    bannerTags: ['INTEL', 'AMD RYZEN'],
    bannerLink: { path: '/dedicated' },
  },
  {
    key: 'infra',
    name: '.Infra',
    link: { path: '/datacenter' },
    items: [
      { id: 'datacenter', icon: 'infra-datacenter', link: { path: '/datacenter' } },
      { id: 'tunnels', icon: 'infra-tunnels', link: { path: '/ip-tunnels' } },
      { id: 'ddos', icon: 'infra-ddos', link: { path: '/antiddos' } },
    ],
    highlightLink: { path: '/datacenter' },
    bannerTags: ['ISO 27001', 'SOC 2'],
    bannerLink: { path: '/datacenter' },
  },
  {
    key: 'web',
    name: '.Web',
    link: { path: '/domains' },
    items: [
      { id: 'domains', icon: 'web-domains', link: { path: '/domains' } },
      { id: 'plesk', icon: 'plesk', link: { path: '/plesk' } },
      { id: 'ssl', icon: 'web-ssl', link: { path: '/ssl' } },
    ],
    highlightLink: { path: '/domains' },
    bannerTags: ['ICANN', 'AFNIC'],
    bannerLink: { path: '/domains' },
  },
  {
    key: 'ent',
    name: '.Entreprise',
    link: { path: '/workspace' },
    items: [
      { id: 'microsoft', icon: 'ent-microsoft', link: { path: '/workspace' } },
      { id: 'google', icon: 'ent-google', link: { path: '/workspace' } },
      { id: 'fibre', icon: 'ent-fibre', link: { path: '/fibre' } },
      { id: 'telephony', icon: 'ent-telephony', link: { path: '/telephonie' } },
    ],
    highlightLink: { path: '/contact' },
    bannerTags: ['M365', 'SIP'],
    bannerLink: { path: '/contact' },
  },
];

const FRENCH = FR as UniversesCopy;

/** Combines the structure with translated copy, falling back to French for any missing text. */
export function localizeUniverses(translation: Translation | UniversesCopy | null | undefined): readonly Universe[] {
  const copy = (translation && typeof translation === 'object' ? translation : {}) as UniversesCopy;
  return STRUCTURE.map((universe) => {
    const local = copy[universe.key] ?? {};
    const fr = FRENCH[universe.key] ?? {};
    const text = <T extends object>(pick: (source: UniverseCopy) => T | undefined, field: keyof T): string =>
      String(pick(local)?.[field] ?? pick(fr)?.[field] ?? '');
    return {
      key: universe.key,
      name: universe.name,
      tagline: local.tagline ?? fr.tagline ?? '',
      description: local.description ?? fr.description ?? '',
      link: universe.link,
      items: universe.items.map((item) => ({
        id: item.id,
        title: text((source) => source.items?.[item.id], 'title'),
        description: text((source) => source.items?.[item.id], 'description'),
        icon: item.icon,
        link: item.link,
      })),
      highlight: {
        label: text((source) => source.highlight, 'label'),
        metric: text((source) => source.highlight, 'metric'),
        caption: text((source) => source.highlight, 'caption'),
        link: universe.highlightLink,
      },
      note: {
        text: text((source) => source.note, 'text'),
        detail: text((source) => source.note, 'detail'),
      },
      banner: {
        title: text((source) => source.banner, 'title'),
        caption: text((source) => source.banner, 'caption'),
        tags: universe.bannerTags,
        link: universe.bannerLink,
      },
    };
  });
}

/** The universes in French, for code that runs outside an injection context (tests, static checks). */
export const UNIVERSES: readonly Universe[] = localizeUniverses(FRENCH);

/**
 * The universes in the active language, reactive to language changes. Must be
 * called in an injection context (e.g. a component field initializer).
 */
export function injectUniverses(): Signal<readonly Universe[]> {
  const translation = translateObjectSignal('anc.universes');
  return computed(() => localizeUniverses(translation()));
}
